import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Chip,
  Alert,
  Typography,
  Pagination,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, Search as SearchIcon } from '@mui/icons-material';
import axios from 'axios';

interface LeavingReasonModel {
  id: string;
  reason: string;
  isActive: boolean;
}

const PAGE_SIZE = 10;

const LeavingReasonMaster: React.FC = () => {
  const [reasons, setReasons] = useState<LeavingReasonModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [formData, setFormData] = useState({
    reason: '',
    isActive: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = s
      ? reasons.filter((r) => r.reason.toLowerCase().includes(s))
      : reasons;
    return list.sort((a, b) => a.reason.localeCompare(b.reason));
  }, [reasons, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  useEffect(() => {
    fetchReasons();
  }, []);

  const fetchReasons = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/system-settings/leaving-reason');
      setReasons(res.data);
    } catch (err) {
      setError('Failed to fetch leaving reasons');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setViewMode(false);
    setFormData({
      reason: '',
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (item: LeavingReasonModel) => {
    setEditingId(item.id);
    setViewMode(false);
    setFormData({
      reason: item.reason,
      isActive: item.isActive,
    });
    setDialogOpen(true);
  };

  const handleView = (item: LeavingReasonModel) => {
    setEditingId(item.id);
    setViewMode(true);
    setFormData({
      reason: item.reason,
      isActive: item.isActive,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this leaving reason?')) return;
    try {
      await axios.delete(`/system-settings/leaving-reason/${id}`);
      fetchReasons();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Cannot delete leaving reason');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/system-settings/leaving-reason/${id}/toggle-status`);
      setReasons((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isActive: !currentStatus } : item
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to toggle status');
      fetchReasons();
    }
  };

  const handleSave = async () => {
    try {
      const payload = { ...formData };
      if (editingId && !viewMode) {
        await axios.patch(`/system-settings/leaving-reason/${editingId}`, payload);
      } else if (!editingId) {
        await axios.post('/system-settings/leaving-reason', payload);
      }
      setDialogOpen(false);
      fetchReasons();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save leaving reason');
    }
  };

  useEffect(() => {
    setPage(1);
  }, [search]);

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Leaving Reasons (TC)</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search reasons"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" /> }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            Create
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Leaving Reason</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center"><CircularProgress /></TableCell>
              </TableRow>
            ) : pagedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">No leaving reasons found.</TableCell>
              </TableRow>
            ) : (
              pagedData.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.reason}</TableCell>
                  <TableCell>
                    <Switch
                      checked={item.isActive}
                      onChange={() => handleToggleStatus(item.id, item.isActive)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleView(item)}><VisibilityIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleEdit(item)}><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(item.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{viewMode ? 'View Reason' : editingId ? 'Edit Reason' : 'New Reason'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 320 }}>
            <TextField
              label="Leaving Reason"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              fullWidth
              required
              disabled={viewMode}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  disabled={viewMode}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          {!viewMode && <Button onClick={handleSave} variant="contained">Save</Button>}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default LeavingReasonMaster;
